import React from 'react';
import { Container, Section } from '@/components/layout/Container';

const LAST_UPDATED = 'February 13, 2026';

export const PrivacyPage: React.FC = () => {
  return (
    <main>
      <Section>
        <Container size="md">
          <h1 className="text-h1 font-bold mb-4">Privacy Policy</h1>
          <p className="text-body text-text-secondary mb-6">
            This policy explains how we collect, use, store, and protect personal data when you use
            this website or contact us regarding professional advisory services.
          </p>
          <p className="text-body-s text-text-tertiary mb-8">Last updated: {LAST_UPDATED}</p>

          <div className="space-y-8 text-body text-text-secondary">
            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Data controller</h2>
              <p>
                The data controller for this website and related advisory inquiries is Christer T.
                Larsen. For all privacy-related questions, contact{' '}
                <a href="mailto:christer@invested.no" className="text-apple-blue hover:underline">
                  christer@invested.no
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Data we collect</h2>
              <div className="space-y-3">
                <p>We may collect the following categories of data:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="text-text-primary font-medium">Inquiry data:</span> name, email,
                    organization, inquiry type, and message content you submit through forms or email.
                  </li>
                  <li>
                    <span className="text-text-primary font-medium">Technical data:</span> IP-derived
                    metadata, browser/device type, approximate region, referrer, pages viewed, and
                    interaction events.
                  </li>
                  <li>
                    <span className="text-text-primary font-medium">Security data:</span> anti-spam
                    and anti-abuse signals used to protect forms and services.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">How we use data</h2>
              <div className="space-y-3">
                <p>We process data to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>respond to inquiries and assess potential engagement fit,</li>
                  <li>communicate about advisory services and next steps,</li>
                  <li>operate, maintain, and secure the website and backend services,</li>
                  <li>
                    analyze traffic and product usage across pages and applications to improve
                    performance, user experience, and conversion funnels.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Legal basis</h2>
              <div className="space-y-3">
                <p>Where applicable, our legal bases are:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="text-text-primary font-medium">Legitimate interests:</span>{' '}
                    operating and securing services, and handling business inquiries.
                  </li>
                  <li>
                    <span className="text-text-primary font-medium">
                      Steps prior to contract:
                    </span>{' '}
                    evaluating and arranging professional engagements upon request.
                  </li>
                  <li>
                    <span className="text-text-primary font-medium">Consent:</span> where required
                    for non-essential analytics or communication preferences.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">
                Cross-application analytics
              </h2>
              <div className="space-y-3">
                <p>
                  We may use analytics tooling across multiple websites and applications to measure
                  page performance, clicks, navigation, and high-level conversion behavior. We use
                  this to understand what works, identify usability issues, and improve outcomes.
                </p>
                <p>Analytics data may include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>page views and route changes,</li>
                  <li>button/link clicks and form interaction events,</li>
                  <li>referrer, device/browser type, and rough geo context,</li>
                  <li>pseudonymous identifiers for returning users where enabled.</li>
                </ul>
                <p>
                  We do not intentionally send sensitive health data or special-category personal
                  data into analytics events.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Analytics providers</h2>
              <div className="space-y-3">
                <p>
                  Depending on configuration, we may use privacy-compliant analytics providers and
                  may integrate Search Console data for SEO insights (for example impressions, CTR,
                  and position).
                </p>
                <p>
                  Provider usage may vary by site, environment, and deployment. Provider processing is
                  subject to each provider’s terms and technical capabilities.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">
                Cookies and similar technologies
              </h2>
              <div className="space-y-3">
                <p>
                  Some analytics configurations are designed to work without marketing cookies, while
                  others may rely on identifiers or storage technologies. Where required by law, we
                  seek consent before enabling non-essential tracking.
                </p>
                <p>
                  You can also use browser controls, privacy settings, and ad/tracking controls to
                  limit collection.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Data sharing</h2>
              <div className="space-y-3">
                <p>We may share data with trusted processors that support service delivery:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>hosting and infrastructure providers,</li>
                  <li>email and communication providers,</li>
                  <li>analytics and monitoring providers,</li>
                  <li>professional advisers where legally required.</li>
                </ul>
                <p>
                  We do not sell personal data. We may disclose data where required by law, court
                  order, or to protect rights, safety, and security.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">International transfers</h2>
              <p>
                Some providers may process data outside your country. Where transfers occur, we use
                appropriate safeguards, such as contractual protections and provider security controls.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Retention</h2>
              <p>
                We retain personal data only for as long as needed for the purposes described in this
                policy, including inquiry handling, service security, and legal compliance. Retention
                periods vary by data type and business/legal requirements.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Security</h2>
              <p>
                We use reasonable technical and organizational measures to protect data against
                unauthorized access, disclosure, alteration, or destruction. No internet transmission
                or storage system is guaranteed to be fully secure.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Your rights</h2>
              <div className="space-y-3">
                <p>
                  Depending on your location, you may have rights to access, correct, delete,
                  restrict, object to processing, or request portability of your personal data.
                </p>
                <p>
                  To exercise these rights, contact{' '}
                  <a href="mailto:christer@invested.no" className="text-apple-blue hover:underline">
                    christer@invested.no
                  </a>
                  . We may need to verify your identity before processing requests.
                </p>
                <p>
                  You may also request details about analytics processing related to your data where
                  applicable.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Children</h2>
              <p>
                This website and services are intended for professional/business audiences and are not
                directed to children.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Medical notice</h2>
              <p>
                This website does not provide personal medical advice, diagnosis, or treatment, and
                website contact does not establish a doctor-patient relationship.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Policy updates</h2>
              <p>
                We may update this policy from time to time. Material changes will be reflected by
                updating the “Last updated” date on this page.
              </p>
            </section>
          </div>
        </Container>
      </Section>
    </main>
  );
};

export default PrivacyPage;
